import { useState } from 'react'

import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import type { AdminHiresFilters, AdminHiresSummary } from '@/features/admin/api/hires.api'
import { useAdminHiresQuery, useAdminHiresSummaryQuery } from '@/features/admin/hooks/useAdminHires'
import type { HiredCandidate } from '@/features/recruiter/api/hires.api'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { formatDate } from '@/utils/format'

const EMPTY_FILTERS = { organizationId: '', programId: '', phase: '', q: '' }

type FilterState = typeof EMPTY_FILTERS

function toParams(f: FilterState): AdminHiresFilters {
  return {
    organization_id: f.organizationId ? Number(f.organizationId) : undefined,
    lms_source_id: f.programId ? Number(f.programId) : undefined,
    academic_phase: f.phase || undefined,
    q: f.q.trim() || undefined,
  }
}

// ─── Summary: hires per recruiter ────────────────────────────────────────────

function RecruiterSummary({
  summary,
  selectedId,
  onSelect,
}: {
  summary: AdminHiresSummary
  selectedId: string
  onSelect: (organizationId: string) => void
}) {
  const max = Math.max(...summary.byOrganization.map((o) => o.hires), 1)

  return (
    <div className="card custom-card">
      <div className="card-header border-bottom d-flex align-items-center justify-content-between">
        <div>
          <p className="fw-semibold fs-15 mb-0">Hires by Recruiter</p>
          <p className="fs-12 text-muted mb-0">Click a recruiter to show only their hires</p>
        </div>
        <div className="text-end">
          <p className="fs-24 fw-bold mb-0 lh-1">{summary.totalHires.toLocaleString()}</p>
          <p className="fs-12 text-muted mb-0">total hires</p>
        </div>
      </div>
      <div className="card-body">
        {summary.byOrganization.length === 0 ? (
          <p className="text-muted fs-13 text-center mb-0 py-2">No hires match these filters.</p>
        ) : (
          <div className="row g-2">
            {summary.byOrganization.map((org) => {
              const id = String(org.organizationId)
              const active = selectedId === id
              return (
                <div key={org.organizationId} className="col-xl-4 col-md-6">
                  <button
                    type="button"
                    className={`btn w-100 text-start p-2 border ${active ? 'border-primary bg-primary-transparent' : 'btn-light'}`}
                    aria-pressed={active}
                    onClick={() => onSelect(active ? '' : id)}
                  >
                    <span className="d-flex align-items-center justify-content-between gap-2 mb-1">
                      <span className="fs-13 fw-medium text-truncate">{org.organizationName}</span>
                      <span className="fs-13 fw-bold">{org.hires.toLocaleString()}</span>
                    </span>
                    <span className="progress progress-xs d-flex">
                      <span className="progress-bar bg-primary" style={{ width: `${(org.hires / max) * 100}%` }} />
                    </span>
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function AdminHiresPage() {
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)

  const debouncedQ = useDebouncedValue(filters.q, 400)
  const params = toParams({ ...filters, q: debouncedQ })

  const { data, isPending, isFetching } = useAdminHiresQuery({ ...params, page, per_page: 10 })
  // The summary always lists every recruiter, so it ignores the recruiter filter.
  const { data: summary } = useAdminHiresSummaryQuery({ ...params, organization_id: undefined })

  const setFilter = (key: keyof FilterState) => (value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const activeFilterCount = Object.values(filters).filter((v) => v !== '').length

  const columns: DataTableColumn<HiredCandidate>[] = [
    {
      key: 'candidate',
      header: 'Candidate',
      render: (row) => (
        <div>
          <p className="fs-13 fw-semibold mb-0">{row.candidateName ?? '—'}</p>
          <p className="fs-12 text-muted mb-0">{row.candidateEmail ?? ''}</p>
        </div>
      ),
    },
    { key: 'job', header: 'Hired For', render: (row) => <span className="fs-13">{row.jobTitle}</span> },
    {
      key: 'recruiter',
      header: 'Recruiter',
      render: (row) => <span className="fs-13 fw-medium">{row.organizationName}</span>,
    },
    {
      key: 'program',
      header: 'Program',
      render: (row) => (
        <span className="badge bg-primary-transparent fs-11" title={row.program}>
          {row.programCode}
        </span>
      ),
    },
    {
      key: 'phase',
      header: 'Phase',
      render: (row) => <span className="fs-13">{row.academicPhase ?? '—'}</span>,
    },
    {
      key: 'hiredAt',
      header: 'Hired On',
      render: (row) => (
        <span className="badge bg-success-transparent fs-12">
          <i className="ti ti-trophy me-1" />
          {formatDate(row.hiredAt)}
        </span>
      ),
    },
  ]

  return (
    <>
      <div className="my-4 page-header-breadcrumb">
        <p className="fw-semibold fs-20 mb-1">Hired Candidates</p>
        <p className="text-muted fs-13 mb-0">Every candidate hired on the platform, across all recruiters.</p>
      </div>

      {summary && (
        <div className="mb-3">
          <RecruiterSummary
            summary={summary}
            selectedId={filters.organizationId}
            onSelect={setFilter('organizationId')}
          />
        </div>
      )}

      <div className="card custom-card">
        <div className="card-header d-flex align-items-center gap-2 flex-wrap">
          <select
            id="hires-filter-program"
            className="form-select form-select-sm fs-13"
            style={{ width: 220 }}
            value={filters.programId}
            onChange={(e) => setFilter('programId')(e.target.value)}
            aria-label="Filter by program"
          >
            <option value="">All programs</option>
            {(summary?.options.programs ?? []).map((p) => (
              <option key={p.id} value={p.id} title={p.name}>{p.code}</option>
            ))}
          </select>
          <select
            id="hires-filter-phase"
            className="form-select form-select-sm fs-13"
            style={{ width: 150 }}
            value={filters.phase}
            onChange={(e) => setFilter('phase')(e.target.value)}
            aria-label="Filter by phase"
          >
            <option value="">All phases</option>
            {(summary?.options.phases ?? []).map((phase) => (
              <option key={phase} value={phase}>{phase}</option>
            ))}
          </select>
          <select
            id="hires-filter-recruiter"
            className="form-select form-select-sm fs-13"
            style={{ width: 220 }}
            value={filters.organizationId}
            onChange={(e) => setFilter('organizationId')(e.target.value)}
            aria-label="Filter by recruiter"
          >
            <option value="">All recruiters</option>
            {(summary?.options.organizations ?? []).map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
          <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
            <span className="input-group-text bg-white">
              <i className="ti ti-search fs-13 text-muted" />
            </span>
            <input
              id="hires-filter-search"
              type="text"
              className="form-control fs-13"
              placeholder="Search name, email or job…"
              value={filters.q}
              onChange={(e) => setFilter('q')(e.target.value)}
              aria-label="Search hired candidates"
            />
          </div>
          {activeFilterCount > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => {
                setFilters(EMPTY_FILTERS)
                setPage(1)
              }}
            >
              <i className="ti ti-filter-off me-1" />
              Clear filters ({activeFilterCount})
            </button>
          )}
          {isFetching && !isPending && (
            <span className="spinner-border spinner-border-sm text-muted" role="status" aria-label="Loading" />
          )}
        </div>

        <DataTable
          columns={columns}
          rows={data?.hires ?? []}
          rowKey={(row) => row.applicationId}
          loading={isPending}
          emptyMessage="No hired candidates match these filters."
          pagination={data?.meta}
          onPageChange={setPage}
        />
      </div>
    </>
  )
}
